import { HttpEventType, HttpResponse } from "@angular/common/http";
import {
  Component,
  ElementRef,
  Renderer2,
  HostListener,
  Injectable,
  Input,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Message, MessageService } from "primeng/api";
import { CreateTaskRequest } from "src/app/model/request/createTaskRequest";
import { ProjectSummaryResponse } from "src/app/model/response/ProjectSummaryResponse";
import { TaskFile, Tasks } from "src/app/model/tasks";
import { TaskStatus } from "src/app/model/enum/status";
import { AuthService } from "src/app/service/auth.service";
import {
  FileService,
  globalUploadProgress$,
} from "src/app/service/file.service";
import { ProjectService } from "src/app/service/project.service";
import { TasksService } from "src/app/service/tasks.service";
import { CustomValidator } from "src/shared/custom-validator";
import { size } from "@ngneat/transloco";
import { TaskParameters } from "src/app/model/response/task-parameters-response";
import { Table } from "primeng/table";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { firstValueFrom, interval } from "rxjs";

/**
 * Segurança: o resultado das tarefas usa `bypassSecurityTrustHtml` e injeta JS da API em `<script>`.
 * O risco real diminui só com API devolvendo dados confiáveis ou refatoração (Plotly só no client + JSON).
 */

@Component({
  selector: "app-tasks",
  templateUrl: "./tasks.component.html",
  styleUrls: ["./tasks.component.css"],
})
@Injectable({ providedIn: "root" })
export class TasksComponent implements OnInit {
  @HostListener("drop", ["$event"])
  onDrop(event: DragEvent) {
    event.preventDefault();
  }
  @HostListener("dragover", ["$event"])
  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  @ViewChild("dtLog") dtLog: Table | undefined;

  userId: string;
  taskList: Tasks[] = [];
  loadingTasksList: boolean = true;
  taskId?: string = "";
  active: number = 0;

  @Input()
  createTask: CreateTaskRequest | null = null;

  displayAddTaskDialog: boolean = false;
  modalpage: string[];
  selectedFiles: File[] | null = null;
  uploadedFiles: any[] = [];
  projectOptions: any[] = [];
  editingTaskId?: string;
  nextBtnLoading: boolean = false;
  keyFiles: any[] = [];
  TaskStatus = TaskStatus;
  loadingSaveTask: boolean = false;
  disableSaveTask: boolean = false;
  public form!: FormGroup;

  startDiagnostic: boolean = false;

  parametersTask?: TaskParameters[];
  selectedParameters: TaskParameters[] = [];
  selectedParameterIds = new Set<number>();

  createdTaskTitle: string = "";

  displayTaskResultDialog: boolean = false;
  htmlcontent: SafeHtml = "";
  loadingTaskResult: boolean = false;
  taskName: string = "";
  uploadMessages: Message[] = [];

  loadingName: string = "Montando resultado do diagnóstico no servidor";
  tabs: { id: string}[] = [];

  activeTab = "";
  progress = 0;

  progressState: string = "";
  progressValue: number = 0;

  statusDiagnostic: string = "";


  constructor(
    private tasksService: TasksService,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private fileService: FileService,
    private projectService: ProjectService,
    private messageService: MessageService,
    private el: ElementRef,
    private renderer: Renderer2,
    private sanitizer: DomSanitizer
  ) {
    this.userId = this.authService.getUserInformations();
    this.modalpage = this.goPage(1);
  }

  ngOnInit() {
    globalUploadProgress$.subscribe((percent) => {
      this.progressValue = percent;
    });
    

    try {
      this.getTaskParameters();
      this.getTaskList(this.userId, "user");
      if (!this.createTask) {
        this.createTask = new CreateTaskRequest();
        this.createForm(this.createTask);
      }
      this.getProject();
    } catch (error) {
      this.showMessage("Erro ao inicializar tarefas.", "error");
    }
  }

  getTaskList(userId: string, param: string) {
    this.tasksService.getTaskList(userId, param).subscribe({
      next: (response) => {
        this.taskList = Array.isArray(response) ? response : [response];

        if (this.taskList) {
          //console.log(this.taskList)
          this.loadingTasksList = false;
        }
      },
      error: (err) => {
        this.showMessage("Erro ao buscar lista de tarefas.", "error");
        this.loadingTasksList = false;
      },
    });
  }

  getStatusLabel(status: string): string {
    return TaskStatus[status as keyof typeof TaskStatus] || status;
  }

  createForm(tasks: CreateTaskRequest) {
    this.form = this.formBuilder.group({
      name: [tasks.name, CustomValidator.getRequired()],
      projectId: [tasks.projectId],
      status: "CR",
      description: [tasks.description],
      analyses: [tasks.analyses || null],
    });
  }

  addTask() {
    this.displayAddTaskDialog = true;
    this.nextBtnLoading = false;
    this.disableSaveTask = true;
  }

  goPage(index: number) {
    let aux = ["none", "none", "none", "none"];
    aux[index - 1] = "";
    return aux;
  }

  backPage(index: number) {
    this.modalpage = this.goPage(index);
    this.active = index - 1;
    this.nextBtnLoading = false;
  }

  async onSelectFile(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    this.selectedFiles = Array.from(input.files);
    input.value = '';

    for (const file of this.selectedFiles) {
      try {
        await this.uploadFileSequentially(file);

        this.progressValue = 0;
        const alreadyExists = this.uploadedFiles.some(
          (f) => f.name === file.name && f.size === file.size
        );

        if (!alreadyExists) {
          this.uploadedFiles.push({
            name: file.name,
            size: file.size,
            href: URL.createObjectURL(file),
            file: file,
          });
        }
      } catch (error) {
        this.showMessage(`Erro ao enviar arquivo ${file.name}.`, "error");
      }
    }
  }

  uploadFileSequentially(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      this.progressState = "start";
      this.loadingSaveTask = true;
      this.disableSaveTask = true;

      const fileEntry = {
        name: file.name,
        size: file.size,
        file: file,
      };

      this.uploadedFiles.push(fileEntry);

      // Rollback do item otimista: em falha, remove da lista para não deixar
      // um anexo "fantasma" (que nunca subiu) aparecendo como enviado.
      const rollback = () => {
        this.uploadedFiles = this.uploadedFiles.filter((f) => f !== fileEntry);
        this.loadingSaveTask = false;
        this.disableSaveTask = false;
        this.progressState = "start";
      };

      this.fileService
        .UploadFiles(file, null, this.userId, null, this.taskId ?? "")
        .then((response) => {
          if (response && 'status' in response && response.status === 200) {
            this.keyFiles.push({
              key: crypto.randomUUID(),
              name: file.name,
              size: file.size,
              persisted: false,
            });
            this.loadingSaveTask = false;
            this.disableSaveTask = false;
            this.progressState = "complete";
            resolve();
          } else {
            rollback();
            reject();
          }
        })
        .catch(() => {
          rollback();
          reject();
        });
    });
  }

    editTask(task: any): void {
    try {
      this.nextBtnLoading = false;
      this.editingTaskId = task.taskId;
      this.taskId = task.taskId;

      this.form.patchValue({
        name: task.name,
        projectId: task.projectId ? task.projectId.split(",") : [],
        description: task.description,
        status: task.status || "CR",
        archives: task.archives || null,
        analyses: task.analyses || null,
      });

      this.uploadedFiles = (task.archives || []).map((archive: any) => ({
        name: archive.originalName,
        size: archive.size,
        href: URL.createObjectURL(new Blob()),
        file: {
          lastModifiedDate: new Date(task.createdDate),
        },
      }));

      this.keyFiles = (task.archives || []).map((archive: any) => ({
        key: archive.file,
        name: archive.originalName,
        size: archive.size,
        persisted: true,
      }));

      this.displayAddTaskDialog = true;
      this.modalpage = this.goPage(1);
    } catch {
      this.showMessage("Erro ao editar tarefa.", "error");
    }
  }

  removeFile(image: any) {
    this.uploadedFiles = this.uploadedFiles.filter((f) => f !== image);
    this.keyFiles = this.keyFiles.filter((f) => f.name !== image.name);
  }

  closeModal() {
    this.displayAddTaskDialog = false;
    this.modalpage = this.goPage(1);
    if (this.form) {
      this.form.reset();
      this.form.patchValue({
        status: "CR",
      });
    }
    this.editingTaskId = undefined;
    this.taskId = "";
    this.uploadedFiles = [];
    this.selectedFiles = null;
    this.keyFiles = [];
    this.selectedParameters = [];

    this.nextBtnLoading = true;
    this.taskName = "";
    this.htmlcontent = this.sanitizer.bypassSecurityTrustHtml("");
    this.loadingName = "Montando resultado do diagnóstico no servidor";

  }

  getProject() {
    this.projectService.findAllProjectSummary(this.userId).subscribe({
      next: (projects: ProjectSummaryResponse[]) => {
        this.projectOptions = projects.map((p) => ({
          id: p.projectID,
          name: p.projectName,
        }));
      },
      error: (err) => {
        this.showMessage("Erro ao buscar projetos.", "error");
      },
    });
  }

  showMessage(message: string, severity: "success" | "error") {
    this.messageService.add({
      key: "toastTask",
      severity,
      summary: severity === "success" ? "Sucesso" : "Erro",
      detail: message,
    });
  }

    async nextPage(): Promise<void> {
    if (!this.form.valid) return;

    this.nextBtnLoading = true;

    let formValue = { ...this.form.value };
    formValue.projectId = Array.isArray(formValue.projectId)
      ? formValue.projectId.join(",")
      : formValue.projectId;

    if (!this.editingTaskId) {
      try {
        const response = await firstValueFrom(
          this.tasksService.createTask(this.userId, formValue)
        );

        if (response.status === 200) {
          const res = response.body;
          this.taskId = res?.taskId ?? "";
          this.editingTaskId = res?.taskId ?? "";
          this.createdTaskTitle = res?.name ?? "";
          this.modalpage = this.goPage(2);
          this.active = 1;
        }
      } catch {
        this.showMessage("Erro ao criar tarefa.", "error");
      } finally {
        this.nextBtnLoading = false;
      }
    } else {
      this.modalpage = this.goPage(2);
      this.active = 1;
      this.nextBtnLoading = false;
    }
  }

    async saveTaskAndFiles(): Promise<void> {
    if (!this.form.valid || !this.editingTaskId) return;

    this.loadingSaveTask = true;
    this.disableSaveTask = true;

    let formValue = { ...this.form.value };
    formValue.projectId = Array.isArray(formValue.projectId)
      ? formValue.projectId.join(",")
      : formValue.projectId;

    formValue.archives = this.keyFiles
      .map(f => `${f.key}|${f.name}|${f.size}|${f.persisted}`)
      .join(",");

    formValue.status = formValue.archives ? "EN" : "CR";

    try {
      const response = await firstValueFrom(
        this.tasksService.updateTask(this.userId, this.editingTaskId, formValue)
      );

      if (response.status === 200) {
        this.getTaskList(this.userId, "user");
        this.showMessage("Tarefa salva com sucesso!", "success");
        this.statusDiagnostic = "success";
      } else {
        this.statusDiagnostic = "error";
      }
    } catch {
      this.showMessage("Erro ao salvar tarefa.", "error");
      this.statusDiagnostic = "error";
    } finally {
      this.resetTaskState();
    }
  }
  private resetTaskState(): void {
    this.loadingSaveTask = false;
    this.disableSaveTask = false;
    this.loadingTasksList = true;
    this.editingTaskId = undefined;
    this.keyFiles = [];
    this.modalpage = this.goPage(3);
  }

  deleteTask(task: any) {
    this.loadingTasksList = true;
    if (task.status == "CR" || task.status == "ER") {
      this.tasksService.deleteTask(this.userId, task.taskId).subscribe({
        next: () => {
          this.getTaskList(this.userId, "user");
          this.showMessage("Tarefa excluída com sucesso!", "success");
        },
        error: (err) => {
          this.loadingTasksList = false;
          this.showMessage("Erro ao excluir tarefa.", "error");
        },
      });
    } else {
      this.showMessage(
        "Tarefa não pode ser excluída, pois já foi iniciada.",
        "error"
      );
      this.loadingTasksList = false;
    }
  }

  getTaskParameters() {
    this.tasksService.getTaskParameters(this.userId).subscribe((response) => {
      this.parametersTask = response;
    });
  }

  onCheckboxChange(event: Event, parameter: any) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedParameters.push(parameter);
    } else {
      this.selectedParameters = this.selectedParameters.filter(
        (p) => p !== parameter
      );
    }
    const formValue = { ...this.form.value };

    if (this.selectedParameters.length > 0) {
      this.startDiagnostic = true;
      const first = this.selectedParameters[0];
      const allParameters = this.selectedParameters
        .flatMap((p) =>
          p.presets.map((preset) =>
            Array.isArray(preset.value) ? preset.value : [preset.value]
          )
        )
        .flat();

      const mergedParameters = Object.assign({}, ...allParameters);

      formValue.analyses = [
        {
          name: first.analysesName,
          parameters: mergedParameters,
        },
      ];
    } else {
      formValue.analyses = {
        name: "",
        parameters: [],
      };
      this.startDiagnostic = false;
    }

    this.form.setValue(formValue);
  }

  isChecked(parameter: any): boolean {
    return this.selectedParameters.includes(parameter);
  }

  setActiveTab(id: string) {
    this.activeTab = id;
    
    const div = this.el.nativeElement.querySelector(`[id="${id}"]`);
    //console.log(div);
    if (div) {
      const allDivs = this.el.nativeElement.querySelectorAll('.plotly-dynamic');
      allDivs.forEach((d: HTMLElement) => {
        this.renderer.setStyle(d, 'display', 'none');
      });
      this.renderer.setStyle(div, "display", "block");
    }
  }

  viewTaskResult(task: any) {
    this.taskId = task.taskId;
    this.taskName = task.name;
    this.loadingTaskResult = true;


    const script2 = this.renderer.createElement("script");
    script2.type = "text/javascript";
    script2.text = 'window.PlotlyConfig = {MathJaxConfig: "local"};';
    this.renderer.appendChild(this.el.nativeElement, script2);

    const script3 = this.renderer.createElement("script");
    script3.type = "text/javascript";

    this.tasksService.getTaskResult(this.userId, this.taskId ?? "").subscribe({
      next: (result) => {
        var jsContent = "";
        let accumulatedHtml = "";
        var display = "block";
        result.js.forEach((graph: any) => {

          const id = Object.keys(graph)[0];
          const content = graph[id];
          const rawHtml = `<div id="${id}" class="plotly-graph-div" style="height:100%; width:100%; display: ${display};"></div>`;
          accumulatedHtml += rawHtml;
          jsContent += content;
          display = "none";
        });
        this.htmlcontent =
          this.sanitizer.bypassSecurityTrustHtml(accumulatedHtml);

        // Aguarda o Angular renderizar o conteúdo HTML antes de executar o script
        setTimeout(() => {
          script3.text = jsContent;
          this.renderer.appendChild(this.el.nativeElement, script3);
          this.loadingTaskResult = false;
        });

        this.tabs = result.js.map((item: { [key: string]: string }) => {
          const id = Object.keys(item)[0];
          return { id };
        });
        this.activeTab = this.tabs[0]?.id;

        this.loadingTaskResult = false;
      },
    });

    this.displayTaskResultDialog = true;
  }

  viewTaskResultProgress(task: any) {
    
    this.taskId = task.taskId;
    this.taskName = task.name;
    this.loadingTaskResult = true;

    interface GraphObject {
      graph: { [guid: string]: string };
    }

    interface ReportObject {
      report: {
        html: string;
        javascript: string;
      };
    }

      //console.log('Script 1 carregado!');

      const script2 = this.renderer.createElement('script');
      script2.type = 'text/javascript';
      script2.text = 'window.PlotlyConfig = {MathJaxConfig: "local"};';
      this.renderer.appendChild(this.el.nativeElement, script2);

    const script3 = this.renderer.createElement("script");
    script3.type = "text/javascript";

    this.progress = 3;
    var loading = interval(200).subscribe(() => {
      if (this.progress < 30) {
        let randomNumber = Math.floor(Math.random() * 5) + 1;
        this.progress += randomNumber; // Incrementa o progresso a cada segundo
      } else {
        loading.unsubscribe(); // Para o intervalo quando o progresso atinge 30%
      }
    });

    this.tasksService
      .getTaskResult(this.userId, this.taskId ?? "")
      .subscribe((event) => {

        if (event.type === HttpEventType.DownloadProgress) {
          if (event.total) {
            this.loadingName = "Carregando em tela o resultado do diagnóstico";
            var pgres = Math.round((100 * event.loaded) / event.total);
            if (pgres > 30) {
              this.progress = pgres;
            }
          }
        } else if (event.type === HttpEventType.Response) {
          const result = event.body;

          var jsContent = "";
          let accumulatedHtml = "";
          var display = "block";
          result.js.forEach((graph: any) => {

            const id = Object.keys(graph)[0];
            const content = graph[id];
            const rawHtml = `<div id="${id}" class="plotly-graph-div" style="height:100%; width:100%; display: ${display};"></div>`;
            accumulatedHtml += rawHtml;
            jsContent += content;
            display = "none";
          });
          this.htmlcontent =
            this.sanitizer.bypassSecurityTrustHtml(accumulatedHtml);

          this.tabs = result.js.map((item: { [key: string]: string }) => {
            const id = Object.keys(item)[0];
            return { id };
          });
          this.activeTab = this.tabs[0]?.id;

          // Aguarda o Angular renderizar o conteúdo HTML antes de executar o script
          setTimeout(() => {
            script3.text = jsContent;
            this.renderer.appendChild(this.el.nativeElement, script3);
            this.loadingTaskResult = false;
          }, 2000);
        }
        if (event.type === HttpEventType.DownloadProgress) {
            if (event.total) {
                this.loadingName = "Carregando em tela o resultado do diagnóstico";
                var pgres = Math.round((100 * event.loaded) / event.total);
                if(pgres > 30){
                  this.progress = pgres;
                }
                //console.log(`Progress: ${pgres}%`);
              
              
            }//console.log(`Progress: ${this.progress}%`);
          } else if (event.type === HttpEventType.Response) {
            const result = event.body;

            var jsContent = "";
            let accumulatedHtml = '';
            var display = "block";

            var height = window.innerHeight * 0.70;
            var width = window.innerWidth * 0.77;

            
            type JsItem = GraphObject | ReportObject;

            let reportCounter = 1;
            result.js.forEach((graph: JsItem) => {

                //console.log(graph);


                if ('graph' in graph) {
                    const guid = Object.keys(graph.graph)[0];
                    const content = graph.graph[guid];


                    const rawHtml = `<div id="${guid}" class="plotly-graph-div plotly-dynamic" style="height:100%; width:100%; display: ${display};"></div>`;
                    accumulatedHtml += rawHtml;

                    const jsRelayout = 'Plotly.relayout("' + guid + '", {width: ' + width + ', height: ' + height + '});';

                    jsContent += content + jsRelayout;
                    display = "none";

                    //console.log('GUID:', guid);
                    //console.log('Conteúdo do graph:', content);



                  } else if ('report' in graph) {
                    const htmlContent = graph.report.html;
                    const js = graph.report.javascript;
                    const rawHtml = `<div id="report-${reportCounter}" class="plotly-dynamic" style="height:100%; width:100%; display: ${display};">${htmlContent}</div>`;

                    accumulatedHtml += rawHtml;

                    jsContent += js;
                    display = "none";

                    //console.log('HTML:', htmlContent);
                    //console.log('JavaScript:', jsContent);

                    reportCounter++;
                  }  

            });
            this.htmlcontent = this.sanitizer.bypassSecurityTrustHtml(accumulatedHtml);

          
            reportCounter = 1;

            this.tabs = result.js.map((item: any) => {
              if ('report' in item) {
                const reportId = `report-${reportCounter++}`;
                return { id: reportId };
              } else {
                  const guid = Object.keys(item.graph)[0];
                return { id: guid };
              }
            });

            //console.log(this.tabs);
            this.activeTab = this.tabs[0]?.id;
            
            //define o estilo dos graficos
            jsContent += `document.querySelectorAll('.plotly-graph-div').forEach((graphDiv) => {
                            // Alterar layout (cores de fundo, eixos, etc.)
                            Plotly.relayout(graphDiv, {
                              'paper_bgcolor': '#242529',
                              'plot_bgcolor': '#242529',
                            
                          'font.color': '#fff',         // Cor geral do texto
                            'xaxis.title.font.color': '#ffffff', // Cor do título do eixo X
                            'yaxis.title.font.color': '#ffffff', // Cor do título do eixo Y
                            'xaxis.tickfont.color': '#ffffff',   // Cor dos ticks do eixo X
                            'yaxis.tickfont.color': '#ffffff',   // Cor dos ticks do eixo Y
                            'legend.font.color': '#ffffff'       // Cor da legenda
                            });
                          });`


            // Aguarda o Angular renderizar o conteúdo HTML antes de executar o script
            setTimeout(() => {
              script3.text = jsContent;
              this.renderer.appendChild(this.el.nativeElement, script3);
               this.loadingTaskResult = false;
            },200);
          }
        });
        this.displayTaskResultDialog = true;
      };  
  }